// UserLayout.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ArrowDown, ArrowUp, MenuIcon, CloseIcon } from '../../../public/icons';
import orderService from '../../../services/orderService';

const UserLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileExpanded, setIsProfileExpanded] = useState(true);
  const [isOrdersExpanded, setIsOrdersExpanded] = useState(true);
  const [isSupportExpanded, setIsSupportExpanded] = useState(true);

  /* 🟢 Pending future gifts count */
  const [pendingFutureGifts, setPendingFutureGifts] = useState(0);

  // Check if we're on memory creation page or memoir details page
  const isMemoryCreationPage = location.pathname.includes('/create-memory');
  const isMemoirDetailsPage =
    location.pathname.includes('/memoirs/') &&
    !location.pathname.includes('/memoirs/create-memoir') &&
    location.pathname !== '/account/memoirs';
  const isMemoirEditPage =
    location.pathname.includes('/memoirs/') &&
    location.pathname.includes('/edit');

  // Redirect from /account to /account/profile
  useEffect(() => {
    if (location.pathname === '/account') {
      navigate('/account/profile', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  /* 🟢 Fetch pending future gifts — refetch on user change + route change */
  useEffect(() => {
    let cancelled = false;

    const countPendingFutureGifts = async () => {
      try {
        const res = await orderService.getMyOrders({ limit: 100 });
        const orders = Array.isArray(res?.data) ? res.data : [];

        const pending = orders.reduce((sum, order) => {
          const gifts =
            order?.items?.[0]?.customization?.futureGift?.futureGifts || [];
          const missing = gifts.filter((g) => !g.hasContent).length;
          return sum + missing;
        }, 0);

        if (!cancelled) setPendingFutureGifts(pending);
      } catch (err) {
        console.error('Failed to count pending future gifts:', err);
        if (!cancelled) setPendingFutureGifts(0);
      }
    };

    if (user) countPendingFutureGifts();

    return () => {
      cancelled = true;
    };
  }, [user, location.key]);

  // Orders menu items with sub-items
  const ordersMenuItems = [
    { name: 'All Orders', path: '/account/orders/all' },
    { name: 'Processing Orders', path: '/account/orders/processing' },
    { name: 'Shipped Orders', path: '/account/orders/shipped' },
    { name: 'Delivered Orders', path: '/account/orders/delivered' },
    { name: 'Cancelled Orders', path: '/account/orders/cancelled' },
  ];

  // Profile menu items
  const profileMenuItems = [
    { name: 'Profile Information', path: '/account/profile' },
    { name: 'Addresses', path: '/account/addresses' },
    { name: 'Notification', path: '/account/notification' },
    { name: 'Security', path: '/account/security' },
  ];

  // Customer Support menu items
  const supportMenuItems = [
    { name: 'Support', href: '/account/support', path: '/support' },
    { name: 'Log Out', isLogout: true },
  ];

  // User initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  };

  const isActive = (path) => {
    if (path === '/account/support') {
      return location.pathname === '/account/support';
    }
    if (path === '/account/memoirs') {
      return location.pathname === '/user/memoir/:memoirId';
    }
    if (path.startsWith('/account/orders')) {
      return location.pathname.startsWith('/account/orders');
    }
    return location.pathname === path;
  };

  const isOrderItemActive = (path) => {
    return location.pathname === path;
  };

  const isProfileItemActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleProfileDropdown = () => {
    setIsProfileExpanded(!isProfileExpanded);
  };

  const toggleOrdersDropdown = () => {
    setIsOrdersExpanded(!isOrdersExpanded);
  };

  const toggleSupportDropdown = () => {
    setIsSupportExpanded(!isSupportExpanded);
  };

  // Full screen layout for memory creation OR memoir details
  if (isMemoryCreationPage || isMemoirDetailsPage || isMemoirEditPage) {
    return (
      <div className="min-h-screen bg-basic">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-basic">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <MenuIcon size={24} color="#1C2A46" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">My Account</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 max-w-[90rem] text-left pt-6 sm:pt-10 md:pt-16 lg:pt-20 pb-12 sm:pb-20 md:pb-32">
        <div className="mb-6 sm:mb-8">
          <div className="text-left">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-secondary">
              My Account
            </h1>
            <h2 className="font-normal text-base sm:text-lg md:text-xl text-[#6B635D] mt-2 sm:mt-4">
              Welcome back, {user?.firstName}
            </h2>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 md:gap-8 border-t-2 border-[#E4E4E4] pt-4 sm:pt-6 lg:pt-0">
          {/* Sidebar - Desktop */}
          <div className="hidden lg:block w-[280px] md:w-[320px] lg:w-[24.5rem] flex-shrink-0 border-[#E4E4E4] lg:border-r-2 pt-6 lg:pt-10">
            <div className="p-4 md:p-6 sticky top-24">
              {/* Sidebar Menu */}
              <nav className="space-y-4 md:space-y-6">
                {/* Profile Section with Dropdown */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Profile
                    </h3>
                    <button
                      onClick={toggleProfileDropdown}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      {isProfileExpanded ? (
                        <ArrowUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ArrowDown className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>

                  {isProfileExpanded && (
                    <ul className="space-y-1">
                      {profileMenuItems.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.path}
                            className={`flex items-center font-normal px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                              isProfileItemActive(item.path)
                                ? 'text-[#1C2A46] font-medium'
                                : 'text-[#6B635D]'
                            }`}
                          >
                            <span className="flex-1">{item.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* My Orders Section with Dropdown */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      My Orders
                    </h3>
                    <button
                      onClick={toggleOrdersDropdown}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      {isOrdersExpanded ? (
                        <ArrowUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ArrowDown className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>

                  {isOrdersExpanded && (
                    <ul className="space-y-1">
                      {ordersMenuItems.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.path}
                            className={`flex items-center justify-between font-normal px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                              isOrderItemActive(item.path)
                                ? 'text-[#1C2A46] font-medium'
                                : 'text-[#6B635D]'
                            }`}
                          >
                            <span>{item.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* 🟢 Future Gifts — dynamic badge */}
                <div>
                  <ul className="space-y-1">
                    <li>
                      <Link
                        to="/account/future-gifts"
                        className={`flex items-center font-normal px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                          isActive('/account/future-gifts')
                            ? 'text-[#1C2A46] font-medium'
                            : 'text-[#6B635D]'
                        }`}
                      >
                        <span className="flex-1">Future Gifts</span>
                        {pendingFutureGifts > 0 && (
                          <span className="bg-[#D4A574] text-white text-xs px-2 py-1 rounded-full min-w-6 h-6 flex items-center justify-center">
                            {pendingFutureGifts}
                          </span>
                        )}
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* Customer Support & Log Out Section */}
                <div className="pt-4 md:pt-6 border-t border-[#E4E4E4]">
                  <ul className="space-y-1">
                    <li>
                      <Link
                        to="/account/support"
                        className={`flex items-center font-normal px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                          isActive('/account/support')
                            ? 'text-[#1C2A46] font-medium'
                            : 'text-[#6B635D]'
                        }`}
                      >
                        <span className="flex-1">Customer Support</span>
                      </Link>
                    </li>
                  </ul>

                  <ul className="space-y-1">
                    <li>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-3 py-2 text-left text-gray-700 rounded-lg transition-colors duration-150 text-sm"
                      >
                        <span className="flex-1">Log Out</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </nav>
            </div>
          </div>

          {/* Mobile Sidebar Overlay */}
          {isMobileMenuOpen && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-xl z-50 transform transition-transform lg:hidden overflow-y-auto">
                <div className="p-5">
                  {/* Mobile Header */}
                  <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <CloseIcon size={20} color="#1C2A46" />
                    </button>
                  </div>

                  {/* Mobile User Info */}
                  <div className="mb-6">
                    <Link
                      to="/account/profile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 mb-4"
                    >
                      <div className="w-12 h-12 rounded-full bg-[#D4A574] flex items-center justify-center text-white font-semibold text-lg">
                        {getUserInitials()}
                      </div>
                      <div>
                        <h2 className="font-semibold text-gray-900">
                          Welcome back, {user?.firstName}
                        </h2>
                        <p className="text-sm text-gray-600 truncate max-w-[180px]">
                          {user?.email}
                        </p>
                      </div>
                    </Link>
                  </div>

                  {/* Mobile Menu */}
                  <nav className="space-y-5">
                    {/* Profile Section */}
                    <div>
                      <button
                        onClick={toggleProfileDropdown}
                        className="flex items-center justify-between w-full text-left mb-2"
                      >
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Profile
                        </h3>
                        {isProfileExpanded ? (
                          <ArrowUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ArrowDown className="w-4 h-4 text-gray-500" />
                        )}
                      </button>

                      {isProfileExpanded && (
                        <ul className="space-y-1 ml-2">
                          {profileMenuItems.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.path}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                                  isProfileItemActive(item.path)
                                    ? 'text-[#1C2A46] font-medium bg-gray-50'
                                    : 'text-gray-700'
                                }`}
                              >
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* My Orders Section */}
                    <div>
                      <button
                        onClick={toggleOrdersDropdown}
                        className="flex items-center justify-between w-full text-left mb-2"
                      >
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          My Orders
                        </h3>
                        {isOrdersExpanded ? (
                          <ArrowUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ArrowDown className="w-4 h-4 text-gray-500" />
                        )}
                      </button>

                      {isOrdersExpanded && (
                        <ul className="space-y-1 ml-2">
                          {ordersMenuItems.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.path}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                                  isOrderItemActive(item.path)
                                    ? 'text-[#1C2A46] font-medium bg-gray-50'
                                    : 'text-gray-700'
                                }`}
                              >
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* 🟢 Future Gifts — dynamic badge */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Future Gifts
                      </h3>
                      <ul className="space-y-1 ml-2">
                        <li>
                          <Link
                            to="/account/future-gifts"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                              isActive('/account/future-gifts')
                                ? 'text-[#1C2A46] font-medium bg-gray-50'
                                : 'text-gray-700'
                            }`}
                          >
                            <span>Future Gifts</span>
                            {pendingFutureGifts > 0 && (
                              <span className="bg-[#D4A574] text-white text-xs px-2 py-1 rounded-full min-w-6 h-6 flex items-center justify-center">
                                {pendingFutureGifts}
                              </span>
                            )}
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Memoir Gallery */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Memoir Gallery
                      </h3>
                      <ul className="space-y-1 ml-2">
                        <li>
                          <Link
                            to="/account/memoirs"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                              isActive('/account/memoirs')
                                ? 'text-[#1C2A46] font-medium bg-gray-50'
                                : 'text-gray-700'
                            }`}
                          >
                            My Memoirs
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Customer Support & Log Out */}
                    <div className="pt-4 border-t border-gray-200">
                      <ul className="space-y-1">
                        <li>
                          <Link
                            to="/account/support"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-3 py-2 rounded-lg transition-colors duration-150 text-sm ${
                              isActive('/account/support')
                                ? 'text-[#1C2A46] font-medium bg-gray-50'
                                : 'text-gray-700'
                            }`}
                          >
                            Customer Support
                          </Link>
                        </li>
                        <li>
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-50"
                          >
                            Log Out
                          </button>
                        </li>
                      </ul>
                    </div>
                  </nav>
                </div>
              </div>
            </>
          )}

          {/* Main Content */}
          <div className="flex-1 pt-4 lg:pt-10 px-2 sm:px-4 lg:px-0">
            <div className="max-w-full overflow-x-hidden">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLayout;